import { useState } from "react";
import { MapPin, ChevronRight, List as ListIcon, Mic, Image as ImageIcon, Receipt, ShieldAlert, Headset, UtensilsCrossed } from "lucide-react";
import { formatTime, BACKEND_URL } from "./chatShared";

const URL_RE = /(https?:\/\/[^\s)]+)/g;
const BOLD_RE = /\*([^*\n]+)\*/g;

/** WhatsApp-style *bold* so the order summary reads the same on web. */
const Bolded = ({ text }) => (
  <>
    {String(text || "").split(BOLD_RE).map((part, i) =>
      i % 2 ? <strong key={i} className="font-bold text-gray-900">{part}</strong> : <span key={i}>{part}</span>,
    )}
  </>
);

const KIND_META = {
  confirm: { label: "Review your order", icon: Receipt, border: "border-l-4 border-l-amber-400" },
  swiggy_link: { label: "Connect Swiggy", icon: UtensilsCrossed, border: "border-l-4 border-l-orange-400" },
  sos: { label: "Emergency", icon: ShieldAlert, border: "border-l-4 border-l-red-500" },
  agent: { label: "Elderra team", icon: Headset, border: "border-l-4 border-l-emerald-500" },
};

const Linkified = ({ text }) => (
  <>
    {String(text || "").split(URL_RE).map((part, i) =>
      URL_RE.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer"
           className="text-[#2D2766] underline font-medium break-all" data-testid="assistant-link">
          {part.includes("/api/l/") ? "Tap here" : part}
        </a>
      ) : <span key={i}><Bolded text={part} /></span>,
    )}
  </>
);

const Chip = ({ label, onClick, disabled, testId, icon }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="min-h-[48px] px-5 py-2.5 rounded-full border-2 border-[#2D2766] text-[#2D2766] bg-white text-base font-semibold
               transition-[background-color,color,transform] duration-150 hover:bg-[#2D2766] hover:text-white active:scale-95
               disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-[#2D2766] flex items-center gap-2"
  >
    {icon}{label}
  </button>
);

const ListCard = ({ list, active, onPick, msgId }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mt-3 rounded-2xl border border-gray-200 bg-white overflow-hidden" data-testid={`assistant-list-${msgId}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-center gap-2 py-3 text-[#2D2766] font-semibold border-b border-gray-100"
              data-testid={`assistant-list-toggle-${msgId}`}>
        <ListIcon className="w-5 h-5" /> {list.button || "Choose"}
      </button>
      {open && list.rows.map((row) => (
        <button key={row.id} type="button" disabled={!active} onClick={() => onPick({ id: row.id, title: row.title })}
                className="w-full text-left px-4 py-3.5 flex items-center justify-between gap-3 border-b last:border-b-0 border-gray-100
                           transition-colors duration-150 hover:bg-[#F3F1FF] disabled:opacity-50 disabled:hover:bg-white"
                data-testid={`assistant-list-row-${row.id}`}>
          <span>
            <span className="block text-base md:text-lg font-semibold text-gray-900">{row.title}</span>
            {row.description && <span className="block text-sm text-gray-500 mt-0.5">{row.description}</span>}
          </span>
          <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
        </button>
      ))}
    </div>
  );
};

/** One assistant/user message with WhatsApp-style buttons, list and location request as big tappable chips. */
export const AssistantBubble = ({ message, active, onButton, onLocation }) => {
  const isUser = message.direction === "in";
  const [locating, setLocating] = useState(false);
  const meta = !isUser ? KIND_META[message.kind] : null;

  const shareLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLocating(false); onLocation(pos.coords.latitude, pos.coords.longitude); },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} message-enter`} data-testid={`msg-${message.id}`}>
      <div className={`${isUser ? "bubble-sent" : "bubble-received"} ${meta?.border || ""} shadow-sm p-3 md:p-4 max-w-[92%]`}>
        {meta && (
          <div className="flex items-center gap-1.5 mb-2 text-[#2D2766]" data-testid={`assistant-kind-${message.kind}`}>
            <meta.icon className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">{meta.label}</span>
          </div>
        )}
        {message.audio_url && (
          <audio controls src={`${BACKEND_URL}${message.audio_url}`} className="mb-2 w-full max-w-xs" data-testid={`assistant-audio-${message.id}`} />
        )}
        {!isUser && message.image && (
          <img src={message.image} alt="Order summary"
               className="mb-2 w-full max-w-sm rounded-xl border border-gray-200" data-testid={`assistant-card-image-${message.id}`} />
        )}
        <p className="text-base md:text-lg leading-relaxed text-gray-900 whitespace-pre-wrap" data-testid={`assistant-text-${message.id}`}>
          <Linkified text={message.text} />
        </p>
        {!isUser && message.cta && (
          <a href={message.cta.url} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 mt-3 min-h-[48px] px-5 py-2.5 rounded-full bg-[#2D2766] text-white text-base font-semibold hover:opacity-90 active:scale-95 transition"
             data-testid={`assistant-cta-${message.id}`}>
            {message.cta.button || "Open"}
          </a>
        )}
        {!isUser && message.buttons?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`assistant-buttons-${message.id}`}>
            {message.buttons.map((b) => (
              <Chip key={b.id} label={b.title} disabled={!active} onClick={() => onButton(b)} testId={`assistant-btn-${b.id}`} />
            ))}
          </div>
        )}
        {!isUser && message.list?.rows?.length > 0 && (
          <ListCard list={message.list} active={active} onPick={onButton} msgId={message.id} />
        )}
        {!isUser && message.location_request && (
          <div className="mt-3">
            <Chip label={locating ? "Finding you…" : "Send location"} disabled={!active || locating} onClick={shareLocation}
                  testId="assistant-send-location-btn" icon={<MapPin className="w-5 h-5" />} />
          </div>
        )}
        <div className={`flex items-center gap-1 mt-1 ${isUser ? "justify-end" : "justify-start"}`}>
          <span className="text-xs text-gray-500">{formatTime(message.created_at)}</span>
          {message.type === "voice" && <Mic className="w-3 h-3 text-gray-400" />}
          {message.type === "image" && <ImageIcon className="w-3 h-3 text-gray-400" />}
        </div>
      </div>
    </div>
  );
};
